/*
 * Measures how loud a stream is, from 0 to 1, every frame. It can keep
 * `--audio-level` up to date on an element, so CSS can draw the speaking
 * indicator.
 */

// How much louder than RMS reads as full volume. Speech rarely gets near 1.
const gain = 4
// Rise fast with the voice and fall back gently between words.
const attack = 0.6
const release = 0.15

interface Meter {
  analyser: AnalyserNode
  samples: Float32Array<ArrayBuffer>
  onLevel: (level: number) => void
  level: number
}

let context: AudioContext | undefined
const meters = new Set<Meter>()
let frame = 0

/** Starts metering the stream's audio onto the element. Returns a function that stops it. */
export function trackAudioLevel(stream: MediaStream, element: HTMLElement): () => void {
  const stop = meterAudio(stream, level => element.style.setProperty('--audio-level', level.toFixed(3)))
  return () => {
    stop()
    element.style.removeProperty('--audio-level')
  }
}

/** Calls `onLevel` with how loud the stream is every frame. Returns a function that stops it. */
export function meterAudio(stream: MediaStream, onLevel: (level: number) => void): () => void {
  if (stream.getAudioTracks().length === 0) return () => {}

  const ctx = audioContext()
  const source = ctx.createMediaStreamSource(stream)
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  source.connect(analyser)

  const meter: Meter = { analyser, samples: new Float32Array(analyser.fftSize), onLevel, level: 0 }
  meters.add(meter)
  if (!frame) frame = requestAnimationFrame(tick)

  return () => {
    source.disconnect()
    meters.delete(meter)
  }
}

function audioContext() {
  if (!context) {
    context = new AudioContext()
    // Browsers may start the context suspended until the user interacts with the page.
    if (context.state === 'suspended') {
      document.addEventListener('click', () => context?.resume(), { once: true })
    }
  }
  return context
}

function tick() {
  for (const meter of meters) {
    meter.analyser.getFloatTimeDomainData(meter.samples)
    let sum = 0
    for (const s of meter.samples) sum += s * s
    const target = Math.min(1, Math.sqrt(sum / meter.samples.length) * gain)
    meter.level += (target - meter.level) * (target > meter.level ? attack : release)
    meter.onLevel(meter.level)
  }
  frame = meters.size > 0 ? requestAnimationFrame(tick) : 0
}
